import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { JobCard } from "@/components/JobCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Globe, Layers, Loader2, MapPin } from "lucide-react";

export default function Jobs() {
  const { user } = useAuth();
  const [states, setStates] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [counts, setCounts] = useState({});
  const [activeState, setActiveState] = useState(null);
  const [filter, setFilter] = useState(null); // "remote" | "multi"
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedIds, setSavedIds] = useState(new Set());

  useEffect(() => { api.get("/states").then(({ data }) => setStates(data)); }, []);
  useEffect(() => {
    if (user?.role === "applicant") api.get("/saved-jobs").then(({ data }) => setSavedIds(new Set(data.map((j) => j.job_id)))).catch(() => {});
  }, [user]);

  const onToggleSave = (jobId, saved) => {
    setSavedIds((prev) => { const n = new Set(prev); saved ? n.add(jobId) : n.delete(jobId); return n; });
  };
  const showSave = user?.role === "applicant";

  useEffect(() => { api.get("/states").then(({ data }) => setStates(data)); }, []);

  const loadGrouped = useCallback(() => {
    setLoading(true);
    api.get("/jobs/grouped").then(({ data }) => { setGrouped(data.groups); setCounts(data.counts); }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadGrouped(); }, [loadGrouped]);

  const runSearch = useCallback((params) => {
    setLoading(true);
    api.get("/jobs", { params }).then(({ data }) => setResults(data)).finally(() => setLoading(false));
  }, []);

  const selectState = (s) => {
    if (activeState === s) return clearAll();
    setActiveState(s); setFilter(null);
    runSearch({ state: s, q: q || undefined });
  };
  const selectFilter = (f) => {
    if (filter === f) return clearAll();
    setActiveState(null); setFilter(f);
    runSearch(f === "remote" ? { remote: true, q: q || undefined } : { multi_state: true, q: q || undefined });
  };
  const clearAll = () => { setActiveState(null); setFilter(null); setResults(null); setQ(""); loadGrouped(); };
  const onSearch = (e) => {
    e.preventDefault();
    if (q || activeState || filter) runSearch({ q: q || undefined, state: activeState || undefined, remote: filter === "remote" || undefined, multi_state: filter === "multi" || undefined });
    else loadGrouped();
  };

  const stateKeys = Object.keys(grouped);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <div className="bg-navy-800 text-white">
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-10">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Browse safety jobs by state</h1>
          <p className="text-slate-300 mt-2">Free to browse. Grouped alphabetically A–Z. Apply with a $10 membership.</p>
          <form onSubmit={onSearch} className="mt-6 flex gap-2 max-w-xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="job-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, company, city…" className="pl-9 bg-white text-navy-800 border-0 h-11" />
            </div>
            <Button type="submit" data-testid="job-search-btn" className="bg-amber-500 hover:bg-amber-600 text-navy-900 font-semibold h-11 px-6">Search</Button>
          </form>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8 flex-1 w-full">
        {/* Filters */}
        <div className="flex flex-wrap gap-2 p-4 bg-white rounded-xl border border-slate-200 shadow-sm mb-8">
          <Button size="sm" variant={filter === "remote" ? "default" : "outline"} data-testid="filter-remote" onClick={() => selectFilter("remote")} className={filter === "remote" ? "bg-emerald-600 hover:bg-emerald-700" : "border-slate-300"}><Globe className="h-3.5 w-3.5 mr-1" /> Remote</Button>
          <Button size="sm" variant={filter === "multi" ? "default" : "outline"} data-testid="filter-multistate" onClick={() => selectFilter("multi")} className={filter === "multi" ? "bg-blue-600 hover:bg-blue-700" : "border-slate-300"}><Layers className="h-3.5 w-3.5 mr-1" /> Multi-State</Button>
          <div className="w-px bg-slate-200 mx-1" />
          {states.map((s) => (
            <button key={s} data-testid={`state-filter-${s}`} onClick={() => selectState(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeState === s ? "bg-navy-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-amber-100 hover:text-amber-800"}`}>
              {s}{counts[s] ? <span className="ml-1 text-xs opacity-70">({counts[s]})</span> : null}
            </button>
          ))}
          {(activeState || filter || results) && (
            <Button size="sm" variant="ghost" data-testid="clear-filters" onClick={clearAll} className="text-amber-600">Clear</Button>
          )}
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>
        ) : results ? (
          <div>
            <h2 className="font-display text-xl font-bold text-navy-800 mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-amber-500" /> {activeState || (filter === "remote" ? "Remote" : filter === "multi" ? "Multi-State" : "Results")} <Badge variant="secondary">{results.length}</Badge>
            </h2>
            {results.length === 0 ? <EmptyState /> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{results.map((j) => <JobCard key={j.job_id} job={j} showSave={showSave} saved={savedIds.has(j.job_id)} onToggleSave={onToggleSave} />)}</div>
            )}
          </div>
        ) : stateKeys.length === 0 ? <EmptyState /> : (
          <div className="space-y-10">
            {stateKeys.map((st) => (
              <div key={st} data-testid={`state-group-${st}`}>
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="font-display text-xl font-bold text-navy-800">{st}</h2>
                  <div className="h-px flex-1 bg-slate-200" />
                  <Badge variant="secondary" className="bg-slate-100">{grouped[st].length} {grouped[st].length === 1 ? "job" : "jobs"}</Badge>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{grouped[st].map((j) => <JobCard key={j.job_id} job={j} showSave={showSave} saved={savedIds.has(j.job_id)} onToggleSave={onToggleSave} />)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

const EmptyState = () => (
  <div className="text-center py-24 bg-white rounded-xl border border-dashed border-slate-300">
    <p className="font-display text-lg font-semibold text-navy-800">No jobs here yet</p>
    <p className="text-slate-500 text-sm mt-1">Check back soon or try another state.</p>
  </div>
);
